import React, { useMemo, useState, useEffect } from "react";
import { Autocomplete, Box, DialogActions, DialogContent, TextField } from "@mui/material";
import TextInputComponent from "../../../atom/Inputs/TextInput";
import TextButtonComponet from "../../../atom/Buttons/TextButton";
import { DatePickerComponent, TimePickerComponent } from "../../../atom";
import { useDispatch } from "react-redux";
import { Actions } from "../../../../core/modules/Actions";
import { connect } from "react-redux";
import dayjs from "dayjs";
import {
  convertStringDate,
} from "../../../../core/Constant";
import WebinarPreviewModal from "../../WebinarPreviewModal";

const initialErrors = {
  name: "",
  date: "",
  time: "",
  duration: "",
  course: "",
  join_url: "",
  timezone: "",
  course_ids: "",
  note: "",
};

const AdminwebinarForm = ({ onClose, courseList, getCourseList, isManualLiveClass = false }) => {
  const dispatch = useDispatch();
  const [newWebinar, setNewWebinar] = useState({
    name: "",
    date: "",
    time: "",
    time_ext: "AM",
    duration: "",
    course: "",
    join_url: "",
    timezone: "Australia/Sydney",
    note: "",
  });

  const [errors, setErrors] = useState(initialErrors);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getCourseList();
  }, []);

  const activeCourseList = useMemo(() => {
    if (!Array.isArray(courseList)) return [];
    return courseList
      .map((c) => ({
        id: Number(c?.id ?? c?.course_id),
        name: c?.name ?? c?.course_name,
      }))
      .filter((c) => Number.isFinite(c.id) && Boolean(c.name));
  }, [courseList]);

  const timezoneOptions = useMemo(() => {
    try {
      const supported = Intl.supportedValuesOf?.("timeZone");
      if (Array.isArray(supported) && supported.length > 0) {
        const normalized = supported.map((timezone) =>
          timezone === "Asia/Calcutta" ? "Asia/Kolkata" : timezone
        );
        const priority = ["Australia/Sydney", "Asia/Kolkata"];
        return [
          ...priority,
          ...normalized.filter((timezone) => !priority.includes(timezone)),
        ];
      }
    } catch {
      console.error("Failed to get timezone options");
    }
    return [
      "Australia/Sydney",
      "Asia/Kolkata",
      "Asia/Dubai",
      "Asia/Singapore",
      "Asia/Tokyo",
      "Asia/Shanghai",
      "Europe/London",
      "Europe/Paris",
      "Europe/Berlin",
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
    ];
  }, []);

  const emptyFormfields = () => {
    setNewWebinar({
      name: "",
      date: "",
      time: "",
      time_ext: "AM",
      duration: "",
      course: "",
      join_url: "",
      timezone: "Australia/Sydney", 
      note: "",
    });
    setSelectedCourseIds([]);
    setErrors(initialErrors);
    setPendingPayload(null);
  };

  const validateForm = () => {
    const newErrors = { ...initialErrors };

    if (!newWebinar?.name) {
      newErrors.name = "Webinar title is required";
    } else if (newWebinar.name.length < 2) {
      newErrors.name = "Title must be at least 2 characters";
    } else if (newWebinar.name.length > 55) {
      newErrors.name = "Title must be at most 55 characters";
    }
    if ((newWebinar?.note || "").length > 100) {
      newErrors.note = "Note must be at most 100 characters";
    }
    if (!newWebinar?.date) {
      newErrors.date = "Date is required";
    } else {
      const selectedDate = dayjs(newWebinar.date).startOf("day");
      const today = dayjs().startOf("day");

      if (selectedDate.isValid() && selectedDate.isBefore(today)) {
        newErrors.date = "Past dates are not allowed";
      }
    }

    if (!newWebinar?.time) {
      newErrors.time = "Time is required";
    }

    if (!newWebinar?.duration) {
      newErrors.duration = "Duration is required";
    }

      const joinUrlTrimmed = (newWebinar?.join_url || "").trim();
      if (!joinUrlTrimmed) {
        newErrors.join_url = "Meeting URL is required";
      } else {
        try {
          const parsed = new URL(joinUrlTrimmed);
          if (!/^https?:$/i.test(parsed.protocol)) {
            newErrors.join_url = "URL must start with http:// or https://";
          }
        } catch {
          newErrors.join_url = "Enter a valid URL (include https:// or http://)";
        }
      }
      if (!(newWebinar.timezone || "").trim()) {
        newErrors.timezone = "Timezone is required";
      }
      if (!Array.isArray(selectedCourseIds) || selectedCourseIds.length === 0) {
        newErrors.course_ids = "Please select at least one course";
    }

    setErrors(newErrors);

    // Return true if there are no errors
    return Object.values(newErrors).every((error) => !error);
  };

  const handleChange = (name, value) => {
    setNewWebinar((prev) => ({
    ...prev,
    [name]: value,
    }));

      setErrors((prev) => ({
        ...prev,
        [name]: name === "name" && value.length > 55
        ? "Title must be at most 55 characters"
        : name === "note" && value.length > 100
          ? "Note must be at most 100 characters"
          : "",
    }));
  };

  const handleAddClick = (event) => {
    if (event) event.preventDefault();

  if (!validateForm()) return;

  const formattedDate = convertStringDate(newWebinar.date);

  let timeStr = "";
  let timeExt = newWebinar.time_ext || "AM";

  if (newWebinar.time?.$d) {
    let hours = newWebinar.time.$H;
    const minutes = newWebinar.time.$m;

    if (hours >= 12) {
      timeExt = "PM";
      if (hours > 12) hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }

    timeStr = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  } else {
    timeStr = newWebinar.time;
  }

  const payload = {
    name: newWebinar.name,
    date: formattedDate,
    time: timeStr,
    time_ext: timeExt,
    duration: Number(newWebinar.duration),
    join_url: (newWebinar.join_url || "").trim(),
    timezone: (newWebinar.timezone || "Australia/Sydney").trim(),
    course_ids: selectedCourseIds,
    note: newWebinar.note,
  };

    setPendingPayload(payload);
    setIsPreviewOpen(true);
  };

  const handleConfirmCreate = () => {
    if (!pendingPayload) return;

    setIsSubmitting(true);

  const submitAction = isManualLiveClass
    ? Actions.webinar.manualCreateWebinarAdmin(pendingPayload)
    : Actions.webinar.createWebinarAdmin(pendingPayload);

  dispatch(submitAction)
    .then((res) => {
        setIsSubmitting(false);
        setIsPreviewOpen(false); 
      if (!res?.error) {
        onClose();
        emptyFormfields();
        dispatch(Actions.webinar.getAllWebinarsAdmin());
      } else {
        console.error(
          isManualLiveClass ? "Manual create webinar failed" : "Create webinar failed",
          res.error
        );
      }
    })
    .catch((err) => {
        setIsSubmitting(false);
        setIsPreviewOpen(false);
      console.error(
        isManualLiveClass ? "Manual create webinar exception" : "Create webinar exception",
        err
      );
    });
};

  const selectedCourseObjects = useMemo(() => {
    return activeCourseList.filter((course) => selectedCourseIds.includes(course.id));
  }, [activeCourseList, selectedCourseIds]);

  const selectedCourseNames = useMemo(() => {
    return selectedCourseObjects.map((c) => c.name).join(", ");
  }, [selectedCourseObjects]);

  return (
    <>
      <form onSubmit={handleAddClick}>
        <DialogContent>
          <Box>      
            <Box>
              <TextInputComponent
                label={"Webinar Title *"}
                placeholder="Enter webinar title"
                name="name"
                value={newWebinar.name}
                onchange={(e) => handleChange("name", e.target.value)}
                readOnly={false}
                isError={Boolean(errors?.name)}
                error={errors?.name}
                required
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextInputComponent
                label={"Meeting URL *"}
                placeholder="https://..."
                    name="join_url"
                    value={newWebinar.join_url}
                    onchange={(e) => handleChange("join_url", e.target.value)}
                    readOnly={false}
                    isError={Boolean(errors?.join_url)}
                    error={errors?.join_url}
                    required
                  />
                </Box>

            <Box>
              <TextInputComponent
                label={"Webinar Note"}
                placeholder="Enter webinar note"
                name="note"
                value={newWebinar.note}
                onchange={(e) => handleChange("note", e.target.value)}
                readOnly={false}
                isError={Boolean(errors?.note)}
                error={errors?.note}
                  />
                </Box>
                <Box sx={{ mt: 2, px: 1.25 }}>
                  <p
                    style={{
                      borderRadius: "10px",
                      padding: 0,
                      margin: 0,
                      marginBottom: 10,
                      color: "#4E657C",
                      fontSize: 19,
                      fontWeight: 700,
                    }}
                  >
                    Select Courses *
                  </p>
                  <Autocomplete
                    multiple
                    options={activeCourseList}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedCourseObjects}
                    onChange={(_, newValue) => {
                      setSelectedCourseIds(newValue.map((item) => item.id));
                  if (newValue.length > 0) {
                      setErrors((prev) => ({ ...prev, course_ids: "" }));
                  }
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, { inputValue }) => {
                      const search = inputValue.trim().toLowerCase();
                      return options.filter((course) =>
                        course.name.toLowerCase().includes(search)
                      );
                    }}
                    noOptionsText="No courses found"
                    sx={{ width: "100%", minWidth: 0 }}
                    slotProps={{
                      paper: { sx: { overflow: "hidden" } },
                      listbox: { sx: { maxHeight: 240, overflowY: "auto" } },
                      popper: {
                        placement: "bottom-start",
                        modifiers: [{ name: "flip", enabled: false }],
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={selectedCourseIds.length === 0 ? "Search and select courses" : ""}
                        error={Boolean(errors?.course_ids)}
                      />
                    )}
                  />
                  {errors?.course_ids ? (
                    <p className="input-error-text">{errors.course_ids}</p>
                  ) : null}
                </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  alignItems: "start",
                  minWidth: 0,
                  mt: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <DatePickerComponent
                    label={"Date *"}
                    selected={newWebinar.date}
                    dateFormat="dd/MM/yyyy"
                    onChange={(e) => handleChange("date", e)}
                    isError={Boolean(errors?.date)}
                    error={errors?.date}
                    minDate={dayjs().startOf("day").toDate()}
              />
            </Box>

              <Box sx={{ minWidth: 0 }}>
                <TimePickerComponent
                  label={"Time *"}
                  value={newWebinar.time}
                  onChange={(e) => handleChange("time", e)}
                  isError={Boolean(errors?.time)}
                  error={errors?.time}
                  />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <TextInputComponent
                    label={"Duration (MIN) *"}
                    placeholder="Enter Webinar duration"
                    name="duration"
                    type="number"
                    value={newWebinar.duration}
                    onchange={(e) => handleChange("duration", e.target.value)}
                    readOnly={false}
                    isError={Boolean(errors?.duration)}
                    error={errors?.duration}
                  />
                </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <p
                    style={{
                      borderRadius: "10px",
                      padding: 0,
                      margin: 0,
                      marginBottom: 10,
                      color: "#4E657C",
                      fontSize: 19,
                      fontWeight: 700,
                    }}
                  >
                    Timezone *
                  </p>
                  <Autocomplete
                    options={timezoneOptions}
                    value={newWebinar.timezone || null}
                    onChange={(_, value) => handleChange("timezone", value || "")}
                    isOptionEqualToValue={(option, value) => option === value}
                    filterOptions={(options, { inputValue }) => {
                      const search = inputValue.trim().toLowerCase();
                      return options.filter((timezone) =>
                        timezone.toLowerCase().includes(search)
                      );
                    }}
                    noOptionsText="No timezones found"
                    sx={{ width: "100%", minWidth: 0 }}
                    slotProps={{
                      paper: { sx: { overflow: "hidden" } },
                      listbox: { sx: { maxHeight: 240, overflowY: "auto" } },
                      popper: {
                        placement: "bottom-start",
                        modifiers: [{ name: "flip", enabled: false }],
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select timezone" error={Boolean(errors?.timezone)} />
                    )}
                  />
                  {errors?.timezone ? (
                    <p className="input-error-text">{errors.timezone}</p>
                  ) : null}
                </Box>
                </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mr: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextButtonComponet
              text={"Add"}
              width={120}
              onButtonClick={handleAddClick}
            />
            <TextButtonComponet
              text={"Cancel"}
              classStyle="btn btn-secondary"
              width={120}
              onButtonClick={() => {
                onClose();
                emptyFormfields();
              }}
            />
          </Box> 
        </DialogActions>
      </form>

      {/* Confirmation & Review Modal */}
      <WebinarPreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        webinarData={pendingPayload}
        courseNames={selectedCourseNames}
        onConfirm={handleConfirmCreate}
        loading={isSubmitting}
      />
    </>
  );
};

export default connect(
  (state) => ({
    courseList: state.students.get("commonCourseList"),
  }),
  {
    getCourseList: Actions.students.getCourseList,
  }
)(AdminwebinarForm);
